<template lang="pug">
div
  .form-field-label(v-if="label")
    span {{ label }}
    span.form-field-require-tag(v-show="required")
      | *
    hint-component(v-if="hint", :text="hint")

  q-input.date-input-class(:value="value", @click.stop="openDateModal()", readonly, :class="inputAdditionalClass")
    template(v-slot:prepend)
      inline-svg.datetime-icon(:src="require(`../../../../assets/icons/calendar.svg`)", @click.stop="openDateModal()")
    template(v-slot:append)
      q-icon.cancel-datetime-field(name="cancel", v-if="showCancelIcon", @click.capture="resetField()")
  .valid-error-message(v-if="invalid") {{ errorMessage }}
  q-dialog#q-datetime-modal(v-model="openModal")
    q-card.row.radius(flat)
      q-date(
        v-model="currentDate",
        :mask="mask",
        :locale="currentDatesLocale",
        :default-view="'Calendar'",
        today-btn,
        :options="optionsFn"
      )
        .row.justify-end
          q-btn.dialog-controls(
            flat,
            @click="openModal = false",
            :label="buttonCancelLabel",
            v-if="type !== 'datetime'"
          )
          q-btn.dialog-controls(flat, @click="checkDate", :label="buttonCloseLabel", v-if="type !== 'datetime'")
      q-time(v-model="currentDate", :mask="mask", format24h, v-if="type === 'datetime'", now-btn)
        .row.justify-end
          q-btn.dialog-controls(
            flat,
            @click="openModal = false",
            :label="buttonCancelLabel",
            v-if="type === 'datetime'"
          )
          q-btn.dialog-controls(flat, @click="checkDate", :label="buttonCloseLabel", v-if="type === 'datetime'")
</template>

<script setup>
import { Datetime } from "vue-datetime";
import { format } from "date-fns";
import { computed, onBeforeUnmount, ref, watch } from "vue";
import { Notify } from "quasar";
import backend from "@/api";
import { currentLocale } from "@/services/useLocales";
import { handleError } from "@/services/handleErrors";
import i18n from "@/plugins/vue-i18n/index";
import {
  buildDateWatchRequestParams,
  dateWatchRequestIsReady,
  parseDateConditionValue,
  validateDateConditions,
} from "@/services/useDateFieldConditions";
import { useStore } from "@/store";
import hintComponent from "../../helpers/hint.vue";

const props = defineProps({
  /** Имя поля из динамической формы. */
  name: { type: String, default: "" },
  /** Параметр текста label над полем. */
  label: { type: String, default: () => "" },
  /** Параметр отвечающий за обязательность поля и отображение *. */
  required: { type: Boolean, default: () => false },
  /** Параметр отвечающий за тип поля date или datetime. Пример: "date" */
  type: { type: String, default: () => "date" },
  /** Параметр значения текущего поля. Пример: "01.01.1970" */
  value: { type: String, default: () => "" },
  /** Параметр назначающий минимальную дату для поля. Пример: "01.01.1970" */
  minDate: { type: String, default: () => "" },
  /** Параметр назначающий максимальную дату для поля. Пример: "01.01.1970" */
  maxDate: { type: String, default: () => "" },
  /** Параметр включающий в себя event из формы расписаний. Используется только там. Пример: 
  {
    "name": "",
    "description": "",
    "periodType": {},
    "period": [],
    "startedAt": "",
    "finishedAt": "",
    "works": {
      "options": [],
      "items": [],
      "toRemove": [],
      "hasNextPage": false,
      "page": 1
    },
    "materials": [],
    "materialsToRemove": [],
    "materialsOptions": [],
    "deadline": "",
    "id": null
  }
   */
  event: { type: Object, default: () => {} },
  /** Параметр отвечающий за отключение поля. Пример: true */
  disable: { type: Boolean, default: () => false },
  /** Флаг невалидности поля из формы. */
  invalid: { type: Boolean, default: false },
  /** Текст ошибки валидации. */
  errorMessage: { type: String, default: "" },
  /** Проп для вывода подсказки */
  hint: { type: String || null, default: () => null },
  /** Параметр включает визуальную обертку поля. */
  withWrapper: { type: Boolean, default: () => false },
  itsFormField: { type: Boolean, default: () => false },
  /** Полный backend-контракт зависимостей поля, включая watch.conditions. */
  watch: { type: Object, default: () => ({}) },
  /** Актуальные значения остальных полей динамической формы. */
  watchValues: { type: Object, default: () => ({}) },
  /** Позволяет форме подменить backend-запрос обработчиком моков. */
  watchRequest: { type: Function, default: null },
  /** Выполняет watch.parents после первого рендера, если все зависимости уже заполнены. */
  watchParentsImmediate: { type: Boolean, default: false },
});

const emit = defineEmits([
  /** Эмит для назначения нового value при выборе новой даты. Пример: "01.01.1970" */
  "select-date",
  "change",
]);
const store = useStore();

const value = ref(props.value);
const openModal = ref(false);
const currentDate = ref("");
let watchRequestId = 0;

const minDate = computed(() => props.minDate);
const maxDate = computed(() => props.maxDate);

const type = computed(() => props.type);
const mask = computed(() => (type.value === "datetime" ? "DD.MM.YYYY HH:mm" : "DD.MM.YYYY"));
const dateFormat = computed(() => (type.value === "datetime" ? "dd.MM.yyyy HH:mm" : "dd.MM.yyyy"));
const buttonCancelLabel = computed(() => i18n["messages"][currentLocale.value]["buttonCancelLabel"]);
const buttonCloseLabel = computed(() => i18n["messages"][currentLocale.value]["buttonCloseLabel"]);
const currentDatesLocale = computed(() => i18n["messages"][currentLocale.value]["date"]);
const inputAdditionalClass = computed(() => ({
  disable: props.disable,
  "date-input-class--with-wrapper": props.withWrapper,
  "valid-error": props.invalid,
}));
const showCancelIcon = computed(() => value.value && !props.disable);

const getCurrentDate = () => {
  const date = new Date();
  return format(date, dateFormat.value);
};

const openDateModal = () => {
  if (props.disable) {
    return;
  }
  let newDateVal = getCurrentDate();

  if (value.value != "") {
    const parsedValue = parseDateValue(value.value);
    newDateVal = parsedValue ? format(parsedValue, dateFormat.value) : value.value;
  }

  currentDate.value = newDateVal;
  openModal.value = true;
};

const checkDate = () => {
  const conditionResult = validateDateConditions({
    value: currentDate.value,
    watch: props.watch,
    values: props.watchValues,
  });

  if (!conditionResult.valid) {
    if (conditionResult.condition?.message) Notify.create(conditionResult.condition.message);

    openModal.value = false;
    resetField();
    return;
  }

  value.value = currentDate.value;
  openModal.value = false;
  emitValue(value.value);
};

const emitValue = val => {
  if (props.event) {
    emit("select-date", { val: val, event: props.event });
    return;
  }
  emit("select-date", val);
  emit("change", val);
};

const resetField = () => {
  value.value = "";
  emitValue(value.value);
};

const parseDateValue = dateValue => parseDateConditionValue(dateValue);

const formatDateValue = dateValue => {
  const parsedDate = parseDateValue(dateValue);
  return parsedDate ? format(parsedDate, dateFormat.value) : dateValue || "";
};

const responseData = response => response?.data?.data || response?.data || response;

const requestWatchValue = async ({ params, parentParams }) => {
  if (props.watchRequest) return props.watchRequest({ fieldName: props.name, params, parentParams });

  const configuredPath = parentParams.path || "";
  const basePath = configuredPath.startsWith("/")
    ? configuredPath
    : store.state.paths[configuredPath] || configuredPath;

  if (!basePath || !parentParams.action) return {};

  return responseData(await backend.index(`${basePath}${parentParams.action}`, { params }));
};

const loadWatchedValue = async parentWatch => {
  if (!dateWatchRequestIsReady(parentWatch, props.watchValues)) return;

  const parentParams = parentWatch.parent_params;
  const resultKey = parentParams.result_key;

  if (!resultKey) return;

  const requestId = ++watchRequestId;
  const params = buildDateWatchRequestParams(parentWatch, props.watchValues);

  try {
    const response = await requestWatchValue({ params, parentParams });

    if (requestId !== watchRequestId) return;

    const data = responseData(response);

    if (!data || !Object.prototype.hasOwnProperty.call(data, resultKey)) return;

    value.value = formatDateValue(data[resultKey]);
    emitValue(value.value);
  } catch (error) {
    if (requestId === watchRequestId) await handleError(error);
  }
};

const getFormattedOptionDate = dateValue => {
  const parsedDate = parseDateValue(dateValue);
  return parsedDate ? format(parsedDate, "yyyy/MM/dd") : "";
};

const isAfterDateValue = (firstValue, secondValue) => {
  const firstDate = parseDateValue(firstValue);
  const secondDate = parseDateValue(secondValue);

  if (!firstDate || !secondDate) {
    return false;
  }

  return firstDate > secondDate;
};

const optionsFn = date => {
  const minOptionDate = getFormattedOptionDate(minDate.value);
  const maxOptionDate = getFormattedOptionDate(maxDate.value);

  if (minOptionDate && maxOptionDate) {
    return date >= minOptionDate && date <= maxOptionDate;
  }

  if (minOptionDate) {
    return date >= minOptionDate;
  } else if (maxOptionDate) {
    return date <= maxOptionDate;
  } else {
    return date > "";
  }
};

watch(minDate, async (newVal, oldVal) => {
  if (newVal === "") {
    // Сброс поля нужен, т.к. изменилось его минимальное значение на пустую строку
    value.value = "";
    emitValue(value.value);
    return;
  }
  if (value.value && isAfterDateValue(newVal, value.value)) {
    value.value = newVal;
    emitValue(value.value);
  }
});

watch(maxDate, async (newVal, oldVal) => {
  if (newVal === "") {
    // Сброс поля нужен, т.к. изменилось его максимальное значение на пустую строку
    value.value = "";
    emitValue(value.value);
    return;
  }
  if (value.value && isAfterDateValue(value.value, newVal)) {
    value.value = newVal;
    emitValue(value.value);
  }
});

watch(
  () => props.value,
  newValue => {
    value.value = formatDateValue(newValue);
  },
);

watch(
  () => (props.watch?.parents || []).map(parent => props.watchValues[parent.parent]),
  async (newValues, oldValues) => {
    const parents = props.watch?.parents || [];
    const changedParent = parents.find((parent, index) => {
      if (!oldValues) return dateWatchRequestIsReady(parent, props.watchValues);

      return JSON.stringify(newValues[index]) !== JSON.stringify(oldValues[index]);
    });

    if (!changedParent) return;

    if (!dateWatchRequestIsReady(changedParent, props.watchValues)) {
      watchRequestId += 1;

      if (value.value) {
        value.value = "";
        emitValue(value.value);
      }

      return;
    }

    await loadWatchedValue(changedParent);
  },
  { deep: true, immediate: props.watchParentsImmediate },
);

onBeforeUnmount(() => {
  // Не позволяем ответу старого экземпляра поля обновить форму после refetch схемы.
  watchRequestId += 1;
});
</script>

<style lang="scss">
@import "../../../../assets/styles/forms/fields/date";

.date-input-class {
  border: none !important;
  cursor: pointer;
  .q-field__prepend {
    padding-left: 15px;
    height: 41px;
    path {
      fill: var(--datetime-icon-fill);
    }
  }
  .q-field__append {
    height: 41px;
    i {
      margin-top: 0;
      margin-right: -1px;
      font-size: 14px;
    }
  }
  .cancel-datetime-field {
    color: var(--field-cancel-icon-color);
  }
  .q-field__control,
  .q-field__control:before,
  .q-field__control:hover:before {
    color: transparent;
    border: none;
  }
  input {
    font-size: 16px;
    margin-top: 0 !important;
  }

  &--with-wrapper {
    border: var(--field-border) !important;
    border-radius: 8px;
    background: var(--field-background);
    min-height: 48px;
    width: 100%;

    .q-field__control {
      min-height: 48px;
      height: 48px;
      padding: 0 14px;
      color: var(--field-input-color);
      background: var(--field-background);
      border-radius: 8px;
    }

    .q-field__control:before,
    .q-field__control:hover:before,
    .q-field__control:after {
      border: none !important;
    }

    .q-field__prepend,
    .q-field__append {
      height: 48px;
    }

    .q-field__prepend {
      padding-left: 0;
      padding-right: 12px;
    }

    input {
      color: var(--field-input-color);
    }
  }

  &.valid-error {
    border: 1px solid red !important;
    border-radius: 8px;
  }

  &.disable {
    cursor: initial;
    .q-field__prepend {
      path {
        fill: #78839c;
      }
    }

    input {
      color: #78839c;
    }
  }
}
#q-datetime-modal {
  .radius {
    border-radius: 20px;
  }
  .q-time,
  .q-date {
    height: 430px;
    box-shadow: none;
    background: var(--vdatetime-popup-background);
    border-radius: 0;
    color: var(--vdatetime-popup-color);
    .q-date__header,
    .q-time__header {
      background-color: var(--vdatetime-popup-day-background-selected);
    }
    .q-time__clock-pointer {
      color: var(--vdatetime-popup-day-background-selected);
    }
    .q-time__clock-position--active {
      background-color: var(--vdatetime-popup-day-background-selected);
    }
    .q-date__content {
      .q-btn {
        padding-left: 5px !important;
        padding-right: 5px !important;
      }
    }
    .q-date__calendar-item {
      .bg-primary {
        background-color: var(--vdatetime-popup-day-background-selected) !important;
      }
    }
    .dialog-controls {
      padding-left: 15px;
      padding-right: 15px;
      color: var(--vdatetime-popup-day-background-selected);
      &:nth-child(1) {
        color: var(--delete-button-color);
      }
    }
  }
  .q-date {
    width: 300px;
    .bg-primary {
      background-color: var(--vdatetime-popup-day-background-selected) !important;
    }
  }
  .q-time {
    .q-time__now-button {
      background-color: var(--vdatetime-popup-day-background-selected);
    }
  }
}
</style>

<style scoped lang="scss">
.valid-error-message {
  position: static;
  margin-top: 4px;
}

.form-field-label-text {
  font-size: 14px;
  font-weight: 600;
  line-height: 27px;
  word-wrap: break-word;
  padding: 1px;
}
</style>
